export function parseEventId(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const id = Number(text);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function presenceTargetValue(targetType, id) {
  return `${targetType}:${id}`;
}

export function parsePresenceTarget(value) {
  const matched = String(value ?? '').match(/^(event|activity):(\d+)$/);
  if (!matched) return null;
  const targetId = Number(matched[2]);
  return targetId > 0 ? { targetType: matched[1], targetId } : null;
}

const brDay = (value) => {
  const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return matched ? `${matched[3]}/${matched[2]}/${matched[1]}` : '';
};

export function fixedLinkNotice(link) {
  const subject = link?.activityName || link?.eventName || 'esta atividade';
  const day = brDay(link?.presenceDay);
  if (link?.presenceDay && link.sameDay === false) {
    return `QR de ${day || 'outro dia'} identificado (${subject}). A presença só pode ser registrada nesse dia, dentro da janela autorizada. Nada foi registrado agora.`;
  }
  if (link?.timing === 'upcoming') {
    return `QR de ${subject} identificado. O evento ainda não começou; a presença só será aceita na janela autorizada. Nada foi registrado agora.`;
  }
  if (link?.timing === 'ended') {
    return `QR de ${subject} identificado. O evento já foi encerrado e a presença não pode mais ser registrada.`;
  }
  return 'QR fixo identificado. Leia o GPS e confirme. A presença ainda não foi registrada.';
}

export function parsePresenceCode(value) {
  const text = String(value ?? '').trim();
  if (!text) return { ok: false, message: 'Informe o PIN ou o código do QR antes de confirmar a presença.' };
  if (text.startsWith('pn1.')) return { ok: true, challenge: text };
  if (/^\d{6}$/.test(text)) return { ok: true, pin: text };
  return { ok: false, message: 'Informe um PIN de 6 dígitos ou o código do QR.' };
}

export function geolocationFailure(error, secureContext = true) {
  if (!secureContext) {
    return 'Para registrar presença, abra o PontoNow em uma conexão HTTPS.';
  }
  if (error?.code === 1) {
    return 'Permissão de localização negada. Habilite o GPS nas configurações do navegador.';
  }
  if (error?.code === 2) return 'Localização indisponível neste aparelho.';
  if (error?.code === 3) return 'A localização demorou demais. Tente novamente.';
  return 'Não foi possível obter a localização.';
}

export function formatEventSchedule(value) {
  if (value == null || value === '') return '—';
  const text = String(value);
  const wall = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (wall && !/[zZ]$|[+-]\d{2}:\d{2}$/.test(text)) {
    return `${wall[3]}/${wall[2]} ${wall[4]}:${wall[5]}`;
  }
  return formatLocalTimestamp(value);
}

export function eventWallClockInput(value) {
  if (value == null || value === '') return '';
  const text = String(value);
  const wall = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (wall && !/[zZ]$|[+-]\d{2}:\d{2}$/.test(text)) {
    return `${wall[1]}T${wall[2]}`;
  }
  const formatted = formatLocalTimestamp(value);
  const shown = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4}), (\d{2}:\d{2})/);
  return shown ? `${shown[3]}-${shown[2]}-${shown[1]}T${shown[4]}` : '';
}

export function eventWallClockPayload(value) {
  const matched = String(value || '').match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return matched ? `${matched[1]} ${matched[2]}:00` : null;
}

export function formatLocalTimestamp(value) {
  if (value == null || value === '') return '—';
  const text = String(value);
  const wall = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (wall && !text.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(text)) {
    return `${wall[3]}/${wall[2]}/${wall[1]}, ${wall[4]}:${wall[5]}`;
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'short',
    hourCycle: 'h23',
  }).format(date);
}

const finiteOrNull = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function eventCenter(event) {
  const latitude = finiteOrNull(event?.latitude);
  const longitude = finiteOrNull(event?.longitude);
  const radius = finiteOrNull(event?.radius_m);
  if (latitude == null || longitude == null || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return null;
  }
  return { lat: latitude, lng: longitude, radius: radius != null && radius > 0 ? radius : null };
}

export function distanceMeters(a, b) {
  const rad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Estimativa exibida ao participante. Quem decide é o servidor na confirmação.
export function assessReading({ center, reading, maxAccuracy, maxAgeSeconds, nowMs }) {
  if (!center || !reading) return null;
  const distance = distanceMeters(center, { lat: reading.latitude, lng: reading.longitude });
  const ageSeconds = Math.max(0, Math.round((nowMs - reading.readAt) / 1000));
  const accuracy = Math.round(reading.accuracy);
  let status = 'inside';
  if (center.radius == null) status = 'unknown';
  else if (maxAccuracy != null && accuracy > maxAccuracy) status = 'imprecise';
  else if (distance > center.radius) status = 'outside';
  return {
    distance: Math.round(distance),
    accuracy,
    ageSeconds,
    stale: maxAgeSeconds != null && ageSeconds > maxAgeSeconds,
    status,
  };
}

const CONFIRMATION_MAX_AGE_SECONDS = 30;

// Barra no aparelho a leitura nova que já chega velha ou imprecisa. O servidor revalida tudo.
export function confirmationReadingProblem({ reading, maxAccuracy, maxAgeSeconds, nowMs }) {
  const latitude = finiteOrNull(reading?.latitude);
  const longitude = finiteOrNull(reading?.longitude);
  if (latitude == null || longitude == null) {
    return 'O GPS não informou sua posição. Tente confirmar de novo.';
  }
  const capturedMs = Date.parse(reading.captured_at);
  const limitSeconds = Math.min(CONFIRMATION_MAX_AGE_SECONDS, finiteOrNull(maxAgeSeconds) ?? CONFIRMATION_MAX_AGE_SECONDS);
  if (!Number.isFinite(capturedMs) || nowMs - capturedMs > limitSeconds * 1000) {
    return 'O GPS devolveu uma leitura antiga. Aguarde alguns segundos ao ar livre e confirme de novo.';
  }
  const accuracy = finiteOrNull(reading.accuracy);
  const accuracyLimit = finiteOrNull(maxAccuracy);
  if (accuracy == null || accuracy <= 0 || (accuracyLimit != null && accuracy > accuracyLimit)) {
    return 'GPS impreciso para validar a presença. Aguarde uma leitura mais precisa e confirme de novo.';
  }
  return null;
}

export function readingStatusLabel(status) {
  if (status === 'inside') return 'Dentro do raio autorizado (estimativa)';
  if (status === 'outside') return 'Fora do raio autorizado';
  if (status === 'imprecise') return 'Precisão do GPS insuficiente';
  return 'Raio do evento não configurado';
}

export function formatAge(seconds) {
  if (seconds < 5) return 'lida agora';
  if (seconds < 60) return `lida há ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  return `lida há ${minutes} min`;
}

const wallTime = (wall) => {
  const matched = String(wall || '').match(/^\d{4}-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
  return matched ? `${matched[2]}/${matched[1]} ${matched[3]}:${matched[4]}` : '—';
};

export function presenceWindowText(windows) {
  if (!windows?.check_in || !windows?.check_out) {
    return 'Horário não configurado: a presença será recusada pelo servidor.';
  }
  return `Entrada: ${wallTime(windows.check_in.opens)} até ${wallTime(windows.check_in.closes)} · `
    + `Saída: ${wallTime(windows.check_out.opens)} até ${wallTime(windows.check_out.closes)} (horário de Brasília)`;
}

// As janelas chegam em horário de Brasília (UTC-3, sem horário de verão).
const wallMs = (wall) => {
  const matched = String(wall || '').match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(:\d{2})?/);
  if (!matched) return null;
  const ms = Date.parse(`${matched[1]}T${matched[2]}${matched[3] || ':00'}-03:00`);
  return Number.isNaN(ms) ? null : ms;
};

const describeWindow = (name, window, nowMs) => {
  const opens = wallMs(window?.opens);
  const closes = wallMs(window?.closes);
  if (opens == null || closes == null) return { state: 'unknown', label: `${name}: horário não configurado` };
  if (nowMs < opens) return { state: 'upcoming', label: `${name} abre ${wallTime(window.opens)}` };
  if (nowMs > closes) return { state: 'closed', label: `${name} encerrada (${wallTime(window.closes)})` };
  return { state: 'open', label: `${name} liberada até ${wallTime(window.closes)}` };
};

// Diferença entre o relógio do servidor (segundos inteiros, Brasília) e o do aparelho.
// Abaixo de 2 s é ruído de arredondamento e rede, e o relógio local é mantido.
export function serverClockOffset(serverWall, receivedAtMs) {
  const serverMs = wallMs(serverWall);
  if (serverMs == null || !Number.isFinite(receivedAtMs)) return 0;
  const offset = serverMs - receivedAtMs;
  return Math.abs(offset) < 2000 ? 0 : offset;
}

// Estimativa pelo relógio corrigido. O servidor aplica as janelas na confirmação.
export function presenceAvailability(windows, nowMs) {
  return {
    checkIn: describeWindow('Entrada', windows?.check_in, nowMs),
    checkOut: describeWindow('Saída', windows?.check_out, nowMs),
  };
}

export const CANCELLATION_COPY = {
  activity: {
    presence: {
      title: 'Inscrição vinculada à presença',
      description: 'Você já registrou presença nesta atividade. Por isso, a inscrição não pode mais ser cancelada.',
    },
    closed: {
      title: 'Prazo de cancelamento encerrado',
      description: 'O cancelamento só é permitido antes do início da atividade.',
    },
  },
  event: {
    presence: {
      title: 'Inscrição vinculada à presença',
      description: 'Existem registros de presença vinculados a este evento. Por isso, a inscrição não pode mais ser cancelada.',
    },
    closed: {
      title: 'Prazo de cancelamento encerrado',
      description: 'O cancelamento só é permitido antes do início do evento e das atividades em que você está inscrito.',
    },
  },
};

const PRESENCE_CODES = new Set(['cancel_presence_recorded', 'cancel_linked_presence']);

// O servidor decide no DELETE; o relógio corrigido só esconde o botão quando o início passa com a tela aberta.
export function cancellationState(target, { nowMs, scope = 'activity', linked = [] } = {}) {
  const copy = CANCELLATION_COPY[scope];
  const code = target?.cancellation?.code;
  if (target?.has_presence || PRESENCE_CODES.has(code) || linked.some((item) => item?.has_presence)) {
    return { state: 'presence', ...copy.presence };
  }
  if (code) return { state: 'closed', ...copy.closed };
  const started = [target, ...linked]
    .map((item) => wallMs(item?.start_datetime))
    .some((startMs) => startMs != null && Number.isFinite(nowMs) && nowMs >= startMs);
  if (started) return { state: 'closed', ...copy.closed };
  return { state: 'allowed', title: null, description: null };
}

export function enrollmentLabel(status) {
  if (status === 'active') return 'Inscrito';
  if (status === 'cancelled') return 'Inscrição cancelada';
  return 'Não inscrito';
}
